import { LocationData, CRIME_TYPES } from '../lib/types'
import { getStateDisplayName } from '../lib/stateLabels'

interface LocationPanelProps {
  location: LocationData
  onClose: () => void
}

const riskLevelColors = {
  low: 'text-green-400 bg-green-400/10',
  medium: 'text-yellow-400 bg-yellow-400/10',
  high: 'text-orange-400 bg-orange-400/10',
  very_high: 'text-red-400 bg-red-400/10',
}

const riskLevelLabels = {
  low: 'Bajo',
  medium: 'Medio',
  high: 'Alto',
  very_high: 'Muy Alto',
}

export default function LocationPanel({ location, onClose }: LocationPanelProps) {
  const hasPopulation = location.population > 0
  const locationName =
    location.type === 'state' ? getStateDisplayName(location.name) : location.name
  const stateName = getStateDisplayName(location.state)
  const topCrimes = Object.entries(location.crimeStats)
    .filter(([key]) => key !== 'total' && key !== 'rate_per_100k' && key !== 'trend' && key !== 'trend_percentage')
    .map(([key, value]) => ({
      id: key,
      name: CRIME_TYPES[key]?.nameEs || key,
      count: value as number,
      color: CRIME_TYPES[key]?.color || '#888',
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 6)

  const maxCount = Math.max(...topCrimes.map((c) => c.count), 1)

  return (
    <div className="w-80 bg-[var(--color-bg-sidebar)] border-l border-[var(--color-border)] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-[var(--color-border)]">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-semibold text-[var(--color-text)] text-lg">
              {locationName}
            </h2>
            {location.type === 'municipality' && (
              <p className="text-sm text-[var(--color-text-muted)]">{stateName}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Risk Level Badge */}
        <div className="mt-3">
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
              riskLevelColors[location.riskLevel]
            }`}
          >
            <span
              className="w-2 h-2 rounded-full mr-1.5"
              style={{
                backgroundColor:
                  location.riskLevel === 'low'
                    ? '#2ed573'
                    : location.riskLevel === 'medium'
                    ? '#ffa502'
                    : location.riskLevel === 'high'
                    ? '#ff7f50'
                    : '#ff4757',
              }}
            />
            Riesgo {riskLevelLabels[location.riskLevel]}
          </span>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="p-4 border-b border-[var(--color-border)] grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">
            Total Delitos
          </p>
          <p className="text-2xl font-bold text-[var(--color-text)]">
            {location.crimeStats.total.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">
            Tasa /100k hab
          </p>
          <p className="text-2xl font-bold text-[var(--color-primary)]">
            {hasPopulation ? location.crimeStats.rate_per_100k.toFixed(1) : '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">
            Población
          </p>
          <p className="text-lg font-medium text-[var(--color-text)]">
            {hasPopulation ? location.population.toLocaleString() : '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">
            Tendencia
          </p>
          <p
            className={`text-lg font-medium flex items-center gap-1 ${
              location.crimeStats.trend === 'up'
                ? 'text-red-400'
                : location.crimeStats.trend === 'down'
                ? 'text-green-400'
                : 'text-yellow-400'
            }`}
          >
            {location.crimeStats.trend === 'up' ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            ) : location.crimeStats.trend === 'down' ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
              </svg>
            )}
            {Math.abs(location.crimeStats.trend_percentage)}%
          </p>
        </div>
      </div>

      {/* Top Crimes */}
      <div className="flex-1 overflow-y-auto p-4">
        <h3 className="text-sm font-medium text-[var(--color-text)] mb-3">
          Principales Delitos
        </h3>
        <div className="space-y-3">
          {topCrimes.map((crime) => (
            <div key={crime.id}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-[var(--color-text)]">{crime.name}</span>
                <span className="text-[var(--color-primary)] font-medium">
                  {crime.count.toLocaleString()}
                </span>
              </div>
              <div className="h-2 bg-[var(--color-bg-card)] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${(crime.count / maxCount) * 100}%`,
                    backgroundColor: crime.color,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-[var(--color-border)] bg-[var(--color-bg-dark)]">
        <p className="text-xs text-[var(--color-text-muted)] text-center">
          Período: Ene 2024 - Ene 2025
        </p>
      </div>
    </div>
  )
}
